import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  DocumentTextIcon, BanknotesIcon, BuildingLibraryIcon, ReceiptPercentIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../context/AuthContext';
import { payrollEngineApi } from '../../../services/api';
import { PageHeader, Spinner, EmptyState, Modal } from '../../../components/shared/index';
import TaxProfileForm from './TaxProfileForm';
import DeductionsList from './DeductionsList';
import BankAccountsList from './BankAccountsList';
import StubDetail from '../engine/StubDetail';

const TABS = [
  { key: 'stubs',     label: 'My Pay Stubs',  icon: ReceiptPercentIcon },
  { key: 'tax',       label: 'Tax Profile',   icon: DocumentTextIcon },
  { key: 'deductions',label: 'Deductions',    icon: BanknotesIcon },
  { key: 'banks',     label: 'Bank Accounts', icon: BuildingLibraryIcon },
];

const STATUS_STYLES = {
  pending:  'bg-gray-100  text-gray-700  border-gray-200',
  issued:   'bg-blue-100  text-blue-800  border-blue-200',
  paid:     'bg-green-100 text-green-800 border-green-200',
  voided:   'bg-red-100   text-red-700   border-red-200',
};

function fmtMoney(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);
}

function StubCard({ stub, onView }) {
  return (
    <div className="card p-5 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-gray-900 truncate">
            {stub.period?.name || stub.period_name || 'Pay period'}
          </h3>
          <span className={clsx(
            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border capitalize',
            STATUS_STYLES[stub.status] || STATUS_STYLES.pending,
          )}>
            {String(stub.status || 'pending').replace('_', ' ')}
          </span>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          {stub.period?.period_start && (
            <>
              {new Date(stub.period.period_start).toLocaleDateString()} →{' '}
              {new Date(stub.period.period_end).toLocaleDateString()}
              {stub.period.pay_date && <> · Pay {new Date(stub.period.pay_date).toLocaleDateString()}</>}
            </>
          )}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-gray-500">Gross</p>
            <p className="text-sm font-semibold text-gray-900">{fmtMoney(stub.gross)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Taxes</p>
            <p className="text-sm font-semibold text-gray-700">
              {fmtMoney((Number(stub.federal_tax) || 0) + (Number(stub.state_tax) || 0) + (Number(stub.fica) || 0))}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Net</p>
            <p className="text-base font-bold text-green-700">{fmtMoney(stub.net)}</p>
          </div>
        </div>
      </div>
      <button onClick={() => onView(stub.id)} className="btn-secondary btn-sm shrink-0">
        View detail
      </button>
    </div>
  );
}

function MyStubs({ onView }) {
  const { data: stubs = [], isLoading } = useQuery({
    queryKey: ['my-stubs'],
    queryFn:  () => payrollEngineApi.myStubs().then(r => r.data || []),
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  if (!stubs.length) {
    return (
      <EmptyState
        title="No pay stubs yet"
        description="Your pay stubs will appear here after your employer runs payroll."
      />
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {stubs.map(s => <StubCard key={s.id} stub={s} onView={onView} />)}
    </div>
  );
}

export default function MyPayroll() {
  const { user } = useAuth();
  const [tab, setTab] = useState('stubs');
  const [stubId, setStubId] = useState(null);

  const employeeId = user?.employee_id || user?.id;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="My Payroll"
        subtitle="View pay stubs, W-4 tax profile, deductions, and direct deposit settings."
      />

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-1 flex-wrap">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                tab === key
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700',
              )}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'stubs'      && <MyStubs onView={setStubId} />}
      {tab === 'tax'        && employeeId && <TaxProfileForm employeeId={employeeId} />}
      {tab === 'deductions' && employeeId && <DeductionsList employeeId={employeeId} />}
      {tab === 'banks'      && employeeId && <BankAccountsList employeeId={employeeId} />}

      {tab !== 'stubs' && !employeeId && (
        <EmptyState
          title="No employee profile linked"
          description="Your account isn't linked to an employee record yet. Ask your HR admin to link it."
        />
      )}

      <Modal open={!!stubId} onClose={() => setStubId(null)} title="Pay stub detail" size="lg">
        {stubId && <StubDetail stubId={stubId} />}
      </Modal>
    </div>
  );
}
